// MUNYA OS · Toast notifications · sistema componentes ui/ v1
//
// API global vía `toast.success(msg)`, `toast.error(msg)`, `toast.info(msg)`.
// Provider obligatorio al raíz de la app (montado en App.tsx).
//
// Visual: esquina inferior derecha · slide-in horizontal · 4s timeout · hover pausa.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle, Warning, Info, XCircle, X } from '@phosphor-icons/react';

export type ToastKind = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
  description?: string;
  durationMs: number;
  createdAt: number;
  remaining: number;
}

interface ToastApi {
  show(
    message: string,
    opts?: { kind?: ToastKind; description?: string; durationMs?: number },
  ): string;
  success(message: string, description?: string): string;
  error(message: string, description?: string): string;
  warning(message: string, description?: string): string;
  info(message: string, description?: string): string;
  dismiss(id: string): void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const ICONS: Record<ToastKind, ReactNode> = {
  success: <CheckCircle size={18} weight="duotone" className="text-[var(--color-munya-olive)]" />,
  error: <XCircle size={18} weight="duotone" className="text-[var(--color-munya-destructive)]" />,
  warning: <Warning size={18} weight="duotone" className="text-[var(--color-munya-warning)]" />,
  info: <Info size={18} weight="duotone" className="text-[var(--color-munya-info)]" />,
};

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [items, setItems] = useState<ToastItem[]>([]);
  const pausedRef = useRef<Set<string>>(new Set());

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, opts?: { kind?: ToastKind; description?: string; durationMs?: number }) => {
      const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const duration = opts?.durationMs ?? 4000;
      const item: ToastItem = {
        id,
        kind: opts?.kind ?? 'info',
        message,
        ...(opts?.description !== undefined ? { description: opts.description } : {}),
        durationMs: duration,
        createdAt: Date.now(),
        remaining: duration,
      };
      setItems((prev) => [...prev, item]);
      return id;
    },
    [],
  );

  const api: ToastApi = {
    show,
    success: (m, d) =>
      show(m, d !== undefined ? { kind: 'success', description: d } : { kind: 'success' }),
    error: (m, d) =>
      show(
        m,
        d !== undefined
          ? { kind: 'error', description: d, durationMs: 6000 }
          : { kind: 'error', durationMs: 6000 },
      ),
    warning: (m, d) =>
      show(m, d !== undefined ? { kind: 'warning', description: d } : { kind: 'warning' }),
    info: (m, d) => show(m, d !== undefined ? { kind: 'info', description: d } : { kind: 'info' }),
    dismiss,
  };

  useEffect(() => {
    if (items.length === 0) return;
    const interval = setInterval(() => {
      setItems((prev) => {
        const now = Date.now();
        return prev
          .map((t) => {
            if (pausedRef.current.has(t.id)) return t;
            const elapsed = now - t.createdAt;
            return { ...t, remaining: Math.max(0, t.durationMs - elapsed) };
          })
          .filter((t) => t.remaining > 0);
      });
    }, 100);
    return () => {
      clearInterval(interval);
    };
  }, [items.length]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm pointer-events-none">
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            aria-live="polite"
            onMouseEnter={() => {
              pausedRef.current.add(t.id);
            }}
            onMouseLeave={() => {
              pausedRef.current.delete(t.id);
            }}
            className="pointer-events-auto flex items-start gap-3 rounded-lg border border-[var(--color-munya-sand)] bg-[var(--color-munya-surface)] shadow-lg p-3 animate-in-toast"
          >
            <div className="shrink-0 mt-0.5">{ICONS[t.kind]}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-tight">{t.message}</p>
              {t.description && <p className="text-xs opacity-70 mt-0.5">{t.description}</p>}
            </div>
            <button
              type="button"
              onClick={() => {
                dismiss(t.id);
              }}
              aria-label="Cerrar"
              className="shrink-0 opacity-50 hover:opacity-100 -mr-1 -mt-1 p-1"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
